import {
  IonButton,
  IonCard,
  IonCardContent,
  IonContent,
  IonDatetime,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonSpinner,
  IonText,
  IonTextarea,
  IonToast,
  IonToggle,
} from "@ionic/react";
import {
  addOutline,
  cameraOutline,
  imagesOutline,
  trashOutline,
} from "ionicons/icons";
import { AxiosError } from "axios";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";

import SelectProject from "@components/SelectProject.tsx";
import { Controller, useForm } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createTimesheet,
  CreateTimesheetRequest,
  Material,
  Project,
  Timesheet,
  updateTimesheet,
  Photo,
} from "@/api";
import { uploadPhoto, deletePhoto } from "@/api/photos";
import { completeProject } from "@/api/projects";
import NumberInput from "@components/NumberInput.tsx";
import { SelectMaterialInput } from "@components/SelectMaterial.tsx";
import { AppHeader } from "../AppHeader";
import { PhotoCard } from "../PhotoCard";
import { report } from "@services/Sentry.ts";
import { useOrganisationSettings } from "@/hooks/useOrganisationSettings";
import { Did } from "@/types";
import { resizeBase64Image } from "@/utils.ts";

type TimesheetForm = Omit<CreateTimesheetRequest, "project_id"> & {
  project_id?: Did | null;
  unknown_work_description?: string;
};

export interface EditTimesheetModalProps {
  timesheet?: Timesheet;
  showModal: boolean;
  handleClose: (props?: {
    invalidate: boolean;
    toast: {
      message: string;
      color: "success" | "danger";
    };
  }) => void;
}

export default function EditTimesheetModal({
  showModal,
  handleClose,
  timesheet,
}: EditTimesheetModalProps) {
  const queryClient = useQueryClient();
  const organisationSettings = useOrganisationSettings();
  const showMaterials =
    organisationSettings?.material_cost_method === "materials";

  // Track selected material details for display
  const [selectedMaterialDetails, setSelectedMaterialDetails] = useState<
    Map<Did, Material>
  >(new Map());

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TimesheetForm>({
    defaultValues: {
      project_id: undefined,
      date: new Date().toISOString(),
      hours: 8,
      note: "",
      unknown_work_description: "",
      materials: [],
    },
  });

  // Watch project_id to conditionally show unknown_work_description field
  const projectId = watch("project_id");
  const isUnknownProject = projectId === null;
  const materials = watch("materials");
  const [showToast, setShowToast] = useState({
    show: false,
    message: "",
    color: "success" as "success" | "danger",
  });
  const [existingPhotos, setExistingPhotos] = useState<Photo[]>([]);
  const [photosToDelete, setPhotosToDelete] = useState<Did[]>([]);
  const [newPhotos, setNewPhotos] = useState<string[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | undefined>(
    timesheet?.project
  );
  const [markCompleted, setMarkCompleted] = useState(false);

  useEffect(() => {
    setSelectedProject(timesheet?.project);
    setValue("project_id", timesheet ? timesheet.project_id : undefined);
    setValue("date", timesheet ? timesheet.date + "T00:00:00" : "");
    setValue("hours", timesheet?.hours || 8);
    setValue("note", timesheet?.note || "");
    setValue(
      "unknown_work_description",
      timesheet?.unknown_work_description || ""
    );
    setValue(
      "materials",
      timesheet?.materials?.map((material) => {
        return {
          id: material.id,
          quantity: material.pivot.quantity,
        };
      }) || []
    );

    // Populate material details for display
    if (timesheet?.materials) {
      const detailsMap = new Map<Did, Material>();
      timesheet.materials.forEach((m) => {
        detailsMap.set(m.id, {
          id: m.id,
          code: m.code ?? "",
          name: m.name,
          description: m.description ?? "",
          price_cents: m.price_cents,
          price_in_euros: m.price_in_euros ?? m.price_cents / 100,
          created_at: "",
          updated_at: "",
        });
      });
      setSelectedMaterialDetails(detailsMap);
    }

    // Initialize existing photos when editing
    setExistingPhotos(timesheet?.photos || []);
    setPhotosToDelete([]);
  }, [setValue, timesheet]);

  const resetForm = useCallback(() => {
    reset();
    setExistingPhotos([]);
    setPhotosToDelete([]);
    setNewPhotos([]);
    setSelectedMaterialDetails(new Map());
    setSelectedProject(undefined);
    setMarkCompleted(false);
  }, [reset]);

  useEffect(() => {
    if (!showModal) {
      resetForm();
    }
  }, [resetForm, showModal]);

  const handleSuccessMutationOrUpdate = async (data: Timesheet) => {
    try {
      // First, delete photos marked for deletion
      if (photosToDelete.length > 0) {
        await Promise.all(
          photosToDelete.map((photoId) => deletePhoto(photoId))
        );
      }

      // Then, upload new photos
      if (newPhotos.length > 0) {
        // Upload photos one-after-another to not bottleneck the connection
        for (const photo of newPhotos) {
          await uploadPhoto({
            project_id: data.project_id ?? null,
            timesheet_id: data.id,
            photo,
          });
        }
      }

      // If the user toggled "project volledig afgerond", mark it completed.
      // The toggle is shown for any timesheet but only effective when there
      // is a real project (not "Onbekend project").
      if (markCompleted && data.project_id) {
        await completeProject(data.project_id);
        await queryClient.invalidateQueries({ queryKey: ["projects"] });
      }
    } catch (error) {
      console.error("Error managing photos:", error);
      report(error);
      setShowToast({
        show: true,
        message: "Uren opgeslagen, maar fout bij foto's",
        color: "danger",
      });
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: ["timesheets"],
      exact: false,
    });
    await queryClient.invalidateQueries({ queryKey: ["dashboard"] });

    const hasPhotoChanges = photosToDelete.length > 0 || newPhotos.length > 0;
    const isEditing = !!timesheet?.id;

    handleClose({
      invalidate: true,
      toast: {
        message: isEditing
          ? hasPhotoChanges
            ? "Uren en foto's bijgewerkt"
            : "Uren bijgewerkt"
          : hasPhotoChanges
          ? "Uren en foto's toegevoegd"
          : "Uren toegevoegd",
        color: "success",
      },
    });
    resetForm();
  };

  const createMutation = useMutation({
    mutationFn: createTimesheet,
    onSuccess: async (data) => {
      await handleSuccessMutationOrUpdate(data);
    },
    onError: (error) => {
      console.error(error);
      let message = "Fout bij toevoegen uren";
      if (error instanceof AxiosError) {
        if (error.response?.data?.message) {
          message = error.response.data.message;
        }
      }
      setShowToast({ show: true, message, color: "danger" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: Did; data: CreateTimesheetRequest }) =>
      updateTimesheet(id, data),
    onSuccess: async (data) => {
      await handleSuccessMutationOrUpdate(data);
    },
    onError: (error) => {
      let message = "Fout bij bijwerken uren";
      if (error instanceof AxiosError) {
        if (error.response?.data?.message) {
          message = error.response.data.message;
        }
      }
      setShowToast({ show: true, message, color: "danger" });
    },
  });

  const doSubmit = (requestData: CreateTimesheetRequest) => {
    if (timesheet?.id) {
      updateMutation.mutate({ id: timesheet.id, data: requestData });
    } else {
      createMutation.mutate(requestData);
    }
  };

  const onSubmit = (data: TimesheetForm) => {
    const hasProject =
      data.project_id !== null && data.project_id !== undefined;
    const isUnknown = data.project_id === null;
    const hasDescription =
      !!data.unknown_work_description &&
      data.unknown_work_description.trim().length > 0;

    if (!hasProject && !isUnknown) {
      setShowToast({
        show: true,
        message: "Selecteer een project",
        color: "danger",
      });
      return;
    }

    if (isUnknown && !hasDescription) {
      setShowToast({
        show: true,
        message: "Vul een omschrijving in voor onbekend project",
        color: "danger",
      });
      return;
    }

    const requestData: CreateTimesheetRequest = {
      project_id: hasProject ? data.project_id : null,
      date: data.date.split("T")[0],
      hours: data.hours,
      note: data.note,
      unknown_work_description: hasProject
        ? undefined
        : data.unknown_work_description,
      materials: showMaterials ? data.materials ?? [] : [],
    };

    doSubmit(requestData);
  };

  const takePhoto = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera,
      });

      if (image.base64String) {
        const photoData = `data:image/${image.format};base64,${image.base64String}`;
        const resizedPhotoData = await resizeBase64Image(photoData);

        setNewPhotos([...newPhotos, resizedPhotoData]);
      }
    } catch (error) {
      console.error("Error taking picture:", error);
      report(error);
      setShowToast({
        show: true,
        message: "Fout bij maken foto",
        color: "danger",
      });
    }
  };

  const selectFromGallery = async () => {
    try {
      const result = await Camera.pickImages({
        quality: 90,
      });

      const resized = await Promise.all(
        // Read the data from the picked images, and resize the images
        result.photos.map(async (image) => {
          const response = await fetch(image.webPath!);
          const blob = await response.blob();
          const dataUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
          return resizeBase64Image(dataUrl);
        })
      );

      setNewPhotos([...newPhotos, ...resized]);
    } catch (error) {
      console.error("Error selecting photo:", error);
      report(error);
      setShowToast({
        show: true,
        message: "Fout bij selecteren foto",
        color: "danger",
      });
    }
  };

  return (
    <>
      <IonModal
        isOpen={showModal}
        onWillPresent={() => {
          if (!timesheet) {
            resetForm();
          }
        }}
        onDidDismiss={() => handleClose()}
        className="timesheets-modal"
      >
        <AppHeader
          title={timesheet?.id ? "Uren bewerken" : "Uren toevoegen"}
          onClose={() => handleClose()}
        />
        <IonContent className="ion-padding">
          <form onSubmit={handleSubmit(onSubmit)}>
            <IonCard>
              <IonCardContent>
                <SelectProject
                  className="ion-margin-bottom"
                  control={control}
                  name="project_id"
                  required={false}
                  disabled={!!timesheet}
                  allowUnknown={!timesheet}
                  selectedProject={selectedProject}
                  onProjectChange={(project) =>
                    setSelectedProject(project ?? undefined)
                  }
                />
                {errors.project_id && (
                  <IonText color="danger" className="error-text">
                    <p>{errors.project_id.message}</p>
                  </IonText>
                )}

                <IonItem className="ion-margin-bottom" lines="none">
                  <IonLabel>
                    <h3>Project volledig afgerond</h3>
                    <p className="ion-text-wrap">
                      Zet aan als dit project geheel klaar is.
                    </p>
                  </IonLabel>
                  <IonToggle
                    slot="end"
                    checked={markCompleted}
                    onIonChange={(e) => setMarkCompleted(e.detail.checked)}
                  />
                </IonItem>

                {/* Show work description field when project is unknown */}
                {isUnknownProject && (
                  <IonItem className="ion-margin-bottom">
                    <IonLabel position="stacked">
                      Omschrijving werk <IonText color="danger">*</IonText>
                    </IonLabel>
                    <Controller
                      name="unknown_work_description"
                      control={control}
                      render={({ field }) => (
                        <IonTextarea
                          {...field}
                          placeholder="Beschrijf het uitgevoerde werk (bijv. locatie, type werkzaamheden)..."
                          rows={3}
                          onIonInput={(e) => field.onChange(e.detail.value)}
                        />
                      )}
                    />
                  </IonItem>
                )}
                <IonList>
                  <IonItem className="ion-margin-bottom">
                    <IonLabel position="stacked">Datum</IonLabel>
                    <Controller
                      name="date"
                      control={control}
                      rules={{ required: "Datum is verplicht" }}
                      render={({ field }) => (
                        <IonDatetime
                          {...field}
                          className="mb-1"
                          presentation="date"
                          disabled={!!timesheet}
                          onIonChange={(e) => field.onChange(e.detail.value)}
                        />
                      )}
                    />
                  </IonItem>
                </IonList>
              </IonCardContent>
              <IonItem className="ion-margin-bottom">
                <IonLabel position="stacked">Aantal uren</IonLabel>
                <Controller
                  name="hours"
                  control={control}
                  rules={{
                    required: "Aantal uren is verplicht",
                    min: { value: 0.5, message: "Minimaal 0.5 uur" },
                    max: { value: 24, message: "Maximaal 24 uur" },
                  }}
                  render={({ field }) => (
                    <NumberInput
                      value={field.value}
                      onChange={field.onChange}
                      min={0.5}
                      max={24}
                      step={0.5}
                      placeholder="8"
                    />
                  )}
                />
              </IonItem>
              {errors.hours && (
                <IonText color="danger" className="error-text">
                  <p>{errors.hours.message}</p>
                </IonText>
              )}

              <IonItem className="ion-margin-bottom">
                <IonLabel position="stacked">Notitie (optioneel)</IonLabel>
                <Controller
                  name="note"
                  control={control}
                  render={({ field }) => (
                    <IonTextarea
                      {...field}
                      placeholder="Voeg een notitie toe..."
                      rows={3}
                      onIonInput={(e) => field.onChange(e.detail.value)}
                    />
                  )}
                />
              </IonItem>
            </IonCard>

            {/* Materials Section */}
            {showMaterials && (
              <IonCard>
                <IonCardContent>
                  <IonLabel className="ion-margin-bottom">
                    <h3>Materialen (optioneel)</h3>
                  </IonLabel>

                  {/* Selected materials list */}
                  {(materials ?? []).length > 0 && (
                    <IonList className="ion-margin-bottom">
                      {(materials ?? []).map((mat, index) => {
                        const detail = selectedMaterialDetails.get(mat.id);
                        return (
                          <IonItem key={mat.id}>
                            <IonLabel>
                              <h3>{detail?.name ?? "Materiaal"}</h3>
                              <p>{mat.quantity}x</p>
                            </IonLabel>
                            <div
                              slot="end"
                              className="tw:flex tw:items-center tw:gap-1"
                            >
                              <IonButton
                                fill="clear"
                                size="small"
                                onClick={() => {
                                  const updated = [...(materials ?? [])];
                                  updated[index] = {
                                    ...updated[index],
                                    quantity: Math.max(
                                      1,
                                      updated[index].quantity - 1
                                    ),
                                  };
                                  setValue("materials", updated);
                                }}
                              >
                                -
                              </IonButton>
                              <span className="tw:text-sm tw:min-w-[1.5rem] tw:text-center">
                                {mat.quantity}
                              </span>
                              <IonButton
                                fill="clear"
                                size="small"
                                onClick={() => {
                                  const updated = [...(materials ?? [])];
                                  updated[index] = {
                                    ...updated[index],
                                    quantity: updated[index].quantity + 1,
                                  };
                                  setValue("materials", updated);
                                }}
                              >
                                +
                              </IonButton>
                              <IonButton
                                fill="clear"
                                color="danger"
                                size="small"
                                onClick={() => {
                                  const updated = (materials ?? []).filter(
                                    (_, i) => i !== index
                                  );
                                  setValue("materials", updated);
                                  const newDetails = new Map(
                                    selectedMaterialDetails
                                  );
                                  newDetails.delete(mat.id);
                                  setSelectedMaterialDetails(newDetails);
                                }}
                              >
                                <IonIcon icon={trashOutline} />
                              </IonButton>
                            </div>
                          </IonItem>
                        );
                      })}
                    </IonList>
                  )}

                  {/* Add material button */}
                  <SelectMaterialInput
                    onMaterialSelect={(material) => {
                      const existing = materials ?? [];
                      const existingIndex = existing.findIndex(
                        (m) => m.id === material.id
                      );

                      if (existingIndex >= 0) {
                        // Increment quantity
                        const updated = [...existing];
                        updated[existingIndex] = {
                          ...updated[existingIndex],
                          quantity: updated[existingIndex].quantity + 1,
                        };
                        setValue("materials", updated);
                      } else {
                        // Add new material
                        setValue("materials", [
                          ...existing,
                          { id: material.id, quantity: 1 },
                        ]);
                      }

                      // Store material details for display
                      setSelectedMaterialDetails(
                        new Map(selectedMaterialDetails).set(
                          material.id,
                          material
                        )
                      );
                    }}
                  >
                    {({ onClick }) => (
                      <IonButton
                        expand="block"
                        fill="outline"
                        onClick={onClick}
                      >
                        <IonIcon icon={addOutline} slot="start" />
                        Materiaal toevoegen
                      </IonButton>
                    )}
                  </SelectMaterialInput>
                </IonCardContent>
              </IonCard>
            )}

            {/* Photo Section */}
            <IonCard>
              <IonCardContent>
                <IonLabel className="ion-margin-bottom">
                  <h3>Foto's (optioneel)</h3>
                </IonLabel>

                {/* Photo Preview Grid */}
                {(existingPhotos.filter((p) => !photosToDelete.includes(p.id))
                  .length > 0 ||
                  newPhotos.length > 0) && (
                  <div className="tw:grid tw:grid-cols-3 tw:gap-2 tw:my-4">
                    {/* Existing photos from server */}
                    {existingPhotos
                      .filter((photo) => !photosToDelete.includes(photo.id))
                      .map((photo) => (
                        <PhotoCard
                          key={photo.id}
                          photo={{ url: photo.url }}
                          onDelete={() => {
                            setPhotosToDelete([...photosToDelete, photo.id]);
                          }}
                        />
                      ))}

                    {/* New photos (Base64) */}
                    {newPhotos.map((photo, index) => (
                      <PhotoCard
                        key={`new-${index}`}
                        photo={{ url: photo }}
                        onDelete={() => {
                          setNewPhotos(newPhotos.filter((_, i) => i !== index));
                        }}
                      />
                    ))}
                  </div>
                )}

                {/* Add Photo Buttons */}
                <div className="tw-flex tw-gap-2 tw:mt-4">
                  <IonButton
                    expand="block"
                    fill="outline"
                    onClick={takePhoto}
                    className="tw-flex-1"
                  >
                    <IonIcon icon={cameraOutline} slot="start" />
                    Maak foto
                  </IonButton>
                  <IonButton
                    expand="block"
                    fill="outline"
                    onClick={selectFromGallery}
                    className="tw-flex-1"
                  >
                    <IonIcon icon={imagesOutline} slot="start" />
                    Galerij
                  </IonButton>
                </div>
              </IonCardContent>
            </IonCard>

            {(createMutation.isError || updateMutation.isError) && (
              <IonText color="danger">
                <p className="ion-padding-horizontal ion-margin-bottom">
                  Er ging iets fout tijdens het opslaan, controleer alle velden
                  en probeer het opnieuw.
                </p>
              </IonText>
            )}

            <div className="form-buttons">
              <IonButton
                expand="block"
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {(createMutation.isPending || updateMutation.isPending) && (
                  <IonSpinner class="tw:mr-4"></IonSpinner>
                )}
                {timesheet?.id ? "Uren bijwerken" : "Uren toevoegen"}
              </IonButton>

              <IonButton
                expand="block"
                fill="outline"
                onClick={() => handleClose()}
              >
                Annuleren
              </IonButton>
            </div>
          </form>
        </IonContent>
      </IonModal>

      {/* Toast */}
      <IonToast
        isOpen={showToast.show}
        onDidDismiss={() => setShowToast({ ...showToast, show: false })}
        message={showToast.message}
        duration={2000}
        color={showToast.color}
      />
    </>
  );
}
